"use client";

import { z } from "zod";
import { SettingsSchema } from "@/schemas/settings.schema";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../ui/collapsible";
import SettingsModal from "./SettingsModal";
import { Input } from "../ui/input";
import { TextArea } from "../ui/textarea";
import { Button } from "../ui/button";
import { FormEvent, useCallback, useEffect, useState } from "react";
import { ToggleInput, ToggleLabel, ToggleSpan } from "../ui/toggle";
import { Label } from "../ui/label";
import ColorPicker from "../ui/colorPicker";
import { MultipleBoolValues } from "@/schemas/multipleBoolValues.schema";
import { getBoolean } from "@/helpers/type.helper";
import { updateSetting } from "@/services/settings.service";

interface Props {
  settings: z.infer<typeof SettingsSchema>[];
  updateData: () => Promise<boolean>;
}

export default function SettingsTable(props: Props) {
  const [sectionsOdd, setSectionsOdd] = useState<
    z.infer<typeof SettingsSchema>[]
  >([]);
  const [sectionsEven, setSectionsEven] = useState<
    z.infer<typeof SettingsSchema>[]
  >([]);
  const [boolValues, setBoolValues] = useState<
    z.infer<typeof MultipleBoolValues>[]
  >([]);


  const resortData = useCallback(
    (data?: z.infer<typeof SettingsSchema>) => {
      if (sectionsOdd.length === 0 && sectionsEven.length === 0) {
        let counter = 1;
        const values: z.infer<typeof MultipleBoolValues>[] = [];
        const tempOdd: z.infer<typeof SettingsSchema>[] = [];
        const tempEven: z.infer<typeof SettingsSchema>[] = [];
        props.settings.map((settings) => {
          if (counter % 2 == 1) {
            tempOdd.push(settings);
            values.push({
              ID: settings.ID.toString(),
              BoolValue: getBoolean(settings.Value),
            });
          } else {
            tempEven.push(settings);
            values.push({
              ID: settings.ID.toString(),
              BoolValue: getBoolean(settings.Value),
            });
          }
          counter++;
        });

        if (tempEven.length > 0 && tempOdd.length > 0 && values.length > 0) {
          setBoolValues(values);
          setSectionsOdd(tempOdd);
          setSectionsEven(tempEven);
        }
      } else if (data != null) {
        if (sectionsEven.length == sectionsOdd.length) {
          setSectionsOdd([...sectionsOdd, data]);
        } else {
          setSectionsEven([...sectionsEven, data]);
        }
      }
    },
    [props.settings, sectionsEven, sectionsOdd]
  );

  useEffect(() => {
    resortData();
  }, [resortData]);

  async function checkSubmit(
    data: FormEvent<HTMLFormElement>,
    settingID: number,
    typeID: number
  ) {
    data.preventDefault();

    // TODO Change to use React Ref instead of getElementById
    const element = document.getElementById(
      settingID.toString()
    ) as HTMLInputElement;
    let value = null;
    if (typeID == 6) {
      value = element.checked;
    } else {
      value = element.value;
    }

    const formData = new FormData();
    formData.append("settingID", settingID.toString());
    formData.append("settingValue", value.toString());
    formData.append("settingType", typeof value);
    await updateSetting(formData);
  }

  const GetBoolValue = useCallback(
    (settingID: string) => {
      return boolValues.find((b) => b.ID == settingID)?.BoolValue;
    },
    [boolValues]
  );

  const ChangeBoolValue = useCallback(
    (settingID: string) => {
      setBoolValues(
        boolValues.map((b) => {
          if (b.ID !== settingID) {
            return b;
          } else {
            return {
              ...b,
              BoolValue: !b.BoolValue,
            };
          }
        })
      );
    },
    [boolValues]
  );

  function TypeHtml(setting: z.infer<typeof SettingsSchema>) {
    return (
      <>
        <form
          onSubmit={(e) => {
            checkSubmit(e, setting.ID, setting.Type);
          }}
        >
          <Label className="text-lg mb-3 text-black dark:text-white">
            {setting.Description}
          </Label>
          {setting.Type == -1 && (
            <Input
              name={setting.Name}
              defaultValue={setting.Value}
              id={setting.ID.toString()}
            />
          )}
          {setting.Type == 0 && (
            <Input
              placeholder={setting.Name}
              name={setting.Name}
              id={setting.ID.toString()}
              defaultValue={setting.Value}
              className="form-control form-control-sm"
            />
          )}
          {setting.Type == 1 && (
            <TextArea
              name={setting.Name}
              id={setting.ID.toString()}
              cols={30}
              rows={4}
              className="form-control form-control-sm h-30"
              defaultValue={setting.Value}
            />
          )}
          {setting.Type == 2 && (
            <input id={setting.ID.toString()} type="file" accept="image/*" />
          )}
          {setting.Type == 3 && (
            <div className="border p-1 rounded-1">
              <ColorPicker
                default_value={setting.Value}
                colorPicker_id={setting.ID.toString()}
              />
            </div>
          )}
          {setting.Type == 4 && (
            <Input
              type="email"
              name={setting.Value}
              id={setting.ID.toString()}
              defaultValue={setting.Value}
              className="form-control form-control-sm"
            />
          )}
          {setting.Type == 5 && (
            <>
              <div
                className="d-flex flex-wrap gap-2"
                id={"multiple-" + setting.ID}
              >
                {setting.Value.split(", ").map((splitValue, index) => (
                  <div
                    key={index}
                    className="input-group input-group-sm multiple-item w-auto"
                    id="item<?=$mkey;?>"
                    data-id="<?=$mkey;?>"
                  >
                    <Input
                      type="text"
                      name="values[<?=$item['S_ID'];?>][value][]"
                      id={setting.ID.toString()}
                      defaultValue={splitValue}
                      className="form-control form-control-sm"
                      placeholder="Nøgleord..."
                      required
                    />
                    <span
                      className="input-group-text text-bg-danger remove-multiple cursor-pointer"
                      data-head={"#multiple-" + index}
                      data-item={"#item-" + index}
                    >
                      <i className="bi bi-trash3-fill"></i>
                    </span>
                  </div>
                ))}
              </div>
              <span
                className="btn btn-success mt-2 btn-sm add-multiple cursor-pointer"
                data-head={"#multiple-" + setting.ID}
              >
                <i className="bi bi-plus-circle-fill mt-2"></i> Tilføj en mere
              </span>
            </>
          )}
          {setting.Type == 6 && (
            <div className="">
              <ToggleLabel className="w-xs">
                Nej
                <ToggleInput
                  className="w-xs"
                  id={setting.ID.toString()}
                  checked={GetBoolValue(setting.ID.toString())}
                  onChange={(_e) => ChangeBoolValue(setting.ID.toString())}
                ></ToggleInput>
                <ToggleSpan />
                Ja
              </ToggleLabel>
            </div>
          )}
          {setting.Type == 7 && (
            <>
              <div className="d-flex gap-2 align-items-center">
                <span>1</span>
                <Input
                  type="range"
                  name={setting.Value}
                  min="1"
                  max="59"
                  onChange={(e) => {
                    document.getElementById("settings-range1")!.innerHTML =
                      e.target.value;
                  }}
                  id={setting.ID.toString()}
                  defaultValue={setting.Value}
                  className="form-range"
                />
                <span>59</span>
              </div>
              <div className="text-center">
                Det valgte:{" "}
                <span id="settings-range1" className="fw-semibold">
                  {setting.Value}
                </span>{" "}
                minutter
              </div>
            </>
          )}
          {setting.Type == 8 && (
            <Input
              type="number"
              name={setting.Value}
              id={setting.ID.toString()}
              defaultValue={setting.Value}
              className="form-control form-control-sm"
            />
          )}
          <br />
          <Button
            type="submit"
            name="edit"
            value="settings"
            className="mt-2 bg-green-500 cursor-pointer"
          >
            Save
          </Button>
        </form>
      </>
    );
  }

  async function handleSuccess(data: z.infer<typeof SettingsSchema>) {
    const result = await props.updateData();
    if (result) {
      resortData(data);
    }

    // Know you hate it, but for some reason, it won't update the settings
    location.reload();
  }

  return (
    <div>
      <div>
        <h1 className="text-xl font-bold text-black dark:text-white mb-2">
          Settings
        </h1>
        <SettingsModal updateData={handleSuccess}></SettingsModal>
      </div>
      {props.settings.map((setting, index) => (
        <Collapsible
          key={index}
          className="group/collapsible rounded-md mt-2 mb-2 white:bg-orange-500 dark:bg-orange-500"
        >
          <CollapsibleTrigger className="py-2 px-4 text-black dark:text-white">
            {setting.Name}
          </CollapsibleTrigger>
          <CollapsibleContent className="py-2 px-4 white:bg-white dark:bg-grey-500 border border-orange-500">
            {TypeHtml(setting)}
          </CollapsibleContent>
        </Collapsible>
      ))}
    </div>
  );
}
